import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Fade, Paper, Skeleton, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import PermissionGuard from "@/components/PermissionGuard";
import { PERMISSIONS } from "@/constants/permissions";

import DeleteDialog from "./components/DeleteDialog";
import EditorComponent from "./components/EditorComponent";
import URLNameInput from "./components/URLNameInput";
import { useActivateCalcPanel, useFetchCustomData, useFetchElementData } from "./hooks/useFetchData.hook";

function Content() {
    const { t } = useTranslation();
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const [nameValue, setNameValue] = useState<string>("");
    const [contentValue, setContentValue] = useState<string>("");
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { customData } = useFetchCustomData();
    const { elementData, loadingPage } = useFetchElementData(id, customData);
    const { activateCalcPanel } = useActivateCalcPanel();

    useEffect(() => {
        if (elementData?.data) {
            setNameValue(elementData.data.name || "");
            if (elementData.data.content) {
                try {
                    const parsedContent = JSON.parse(elementData.data.content);
                    setContentValue(JSON.stringify(parsedContent, null, 4));
                } catch (error) {
                    console.error("Failed to parse content:", error);
                    setContentValue(elementData.data.content || "");
                }
            }
        }
    }, [elementData]);

    const handleCloseModal = () => setDeleteModalOpen(false);
    const handleOpenModal = () => setDeleteModalOpen(true);

    const handleNameChange = (newName: string) => {
        setNameValue(newName);
    };

    const handleContentChange = (newContent: string) => {
        setContentValue(newContent);
    };

    const handleSave = async () => {
        if (!elementData?.data?.url) return;

        setLoadingSave(true);
        try {
            activateCalcPanel(
                {
                    url: `https://${elementData.data.url}`,
                    data: {
                        content: contentValue,
                        name: nameValue,
                    },
                },
                {
                    onSuccess: () => {
                        setLoadingSave(false);
                    },
                    onError: (error) => {
                        console.error(error);
                        setLoadingSave(false);
                    },
                }
            );
        } catch (error) {
            console.error(error);
            setLoadingSave(false);
        }
    };

    useEffect(() => {
        setLoadingDelete(false);
        setDeleteModalOpen(false);
        setLoadingSave(false);
    }, [id]);

    const renderContent = useMemo(() => {
        if (!elementData) {
            return null;
        }

        return (
            <>
                <URLNameInput elementData={elementData} onNameChange={handleNameChange} />
                <EditorComponent key={id} elementData={elementData} onContentChange={handleContentChange} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }} className="actionButtons">
                    {!elementData?.data?.activated && (
                        <PermissionGuard permissions={[PERMISSIONS.MISC_SVC_CALCULATORS_UPDATE]} mode="any">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                size="small"
                                onClick={handleSave}
                                disabled={loadingSave}
                            >
                                {loadingSave ? t("saving") : t("save")}
                            </Button>
                        </PermissionGuard>
                    )}
                    <PermissionGuard permissions={[PERMISSIONS.MISC_SVC_CALCULATORS_DELETE]} mode="any">
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleOpenModal}
                            startIcon={<DeleteIcon />}
                            size="small"
                        >
                            {t("delete")}
                        </Button>
                    </PermissionGuard>
                </Box>
            </>
        );
    }, [elementData, id, loadingSave, handleNameChange, handleContentChange, t]);

    if (loadingPage) {
        return (
            <Fade in={loadingPage} timeout={300}>
                <Box display="flex" flexDirection="column" gap={2} p={3} height="100%">
                    <Skeleton variant="rectangular" width="100%" height={50} />
                    <Skeleton variant="rectangular" width="100%" height="calc(100vh - 200px)" />
                </Box>
            </Fade>
        );
    }

    if (!elementData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={3}>
                <Typography variant="h6" color="text.secondary">
                    No data found. Please select a different item.
                </Typography>
            </Box>
        );
    }

    return (
        <Fade in={!loadingPage} timeout={300}>
            <Paper
                elevation={0}
                className="Content"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflowY: "auto",
                    height: "100%",
                    p: 3,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                }}
            >
                <Box
                    className="underContent"
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                    }}
                >
                    {renderContent}
                </Box>
                <DeleteDialog
                    open={deleteModalOpen}
                    handleClose={handleCloseModal}
                    elementData={elementData}
                    loadingDelete={loadingDelete}
                    setLoadingDelete={setLoadingDelete}
                    navigate={navigate}
                />
            </Paper>
        </Fade>
    );
}

export default Content;
